import React, { useMemo } from 'react';
import { AbsoluteFill, Sequence, Video, useCurrentFrame, useVideoConfig, interpolate } from 'remotion';
import type { Caption, CaptionWord } from '../schemas';
import { resolveMediaSrc } from '../shared/media';
import { FONTS } from '../brand/fonts';

// Slide-cutaway defaults + look. The dark field matches the slide decks' own
// background (#0b1324) so the 16:9 slide reads seamless inside the 9:16 frame.
const SLIDE_CUTAWAY_DEFAULT_AT_SEC = 2.0;
const SLIDE_CUTAWAY_DEFAULT_DUR_SEC = 3.0;
const SLIDE_FIELD_COLOR = '#0b1324';

export type TalkingHeadEnhancedProps = {
  videoUrl: string;
  captions?: Caption;
  durationInFrames?: number;
  fitMode?: 'cover' | 'blur-contain';
  captionStyle?: {
    fontFamily?: string;
    fontSize?: number;
    color?: string;
    highlightColor?: string;
    backgroundColor?: string;
  };
  // Optional brief full-screen slide cutaway: at `atSec` the face is covered
  // by the slide clip (16:9 full-width, centered vertically on the dark field)
  // for `durSec` seconds. The face <Video> stays mounted so its audio is never
  // interrupted; captions keep rendering on top.
  slideVideoUrl?: string;
  slideCutaway?: {
    atSec?: number;
    durSec?: number;
  };
};

type CaptionChunk = {
  words: CaptionWord[];
  startMs: number;
  endMs: number;
};

const isSentenceBreak = (w: string): boolean => /[.!?]\s*$/.test(w);

function buildCaptionChunks(words: CaptionWord[]): CaptionChunk[] {
  const out: CaptionChunk[] = [];
  const maxWords = 6;
  const maxChars = 30;

  let cur: CaptionWord[] = [];
  let chars = 0;

  const flush = () => {
    if (cur.length === 0) return;
    out.push({
      words: cur,
      startMs: cur[0]!.startMs,
      endMs: cur[cur.length - 1]!.endMs,
    });
    cur = [];
    chars = 0;
  };

  for (const raw of words) {
    const text = (raw.text || '').trim();
    if (!text) continue;
    const nextChars = chars + text.length + 1;
    const shouldWrap = cur.length >= maxWords || nextChars > maxChars;
    if (shouldWrap) flush();
    cur.push({ ...raw, text });
    chars = chars + text.length + 1;
    if (isSentenceBreak(text) && cur.length >= 4) flush();
  }
  flush();
  return out;
}

const pickActiveIndex = (words: CaptionWord[], currentMs: number): number => {
  if (words.length === 0) return -1;

  for (let i = 0; i < words.length; i++) {
    const w = words[i]!;
    if (currentMs >= w.startMs && currentMs <= w.endMs) return i;
    if (currentMs < w.startMs) return Math.max(0, i - 1);
  }
  return words.length - 1;
};

const KaraokeCaptions: React.FC<{
  captions: Caption;
  currentMs: number;
  style?: TalkingHeadEnhancedProps['captionStyle'];
}> = ({ captions, currentMs, style }) => {
  const { width, height } = useVideoConfig();
  const isPortrait = height / width >= 1.25;

  const chunks = useMemo(() => buildCaptionChunks(captions.words || []), [captions.words]);
  if (!chunks.length) return null;

  const activeChunkIndex = (() => {
    for (let i = 0; i < chunks.length; i++) {
      const c = chunks[i]!;
      if (currentMs >= c.startMs && currentMs <= c.endMs) return i;
      if (currentMs < c.startMs) return Math.max(0, i - 1);
    }
    return chunks.length - 1;
  })();

  const chunk = chunks[activeChunkIndex]!;
  const activeWordIndex = pickActiveIndex(chunk.words, currentMs);

  const fontFamily = style?.fontFamily || FONTS.body;
  const fontSize = style?.fontSize || (isPortrait ? 56 : 50);
  const color = style?.color || '#ffffff';
  const highlightColor = style?.highlightColor || '#00d1ff';
  const bg = style?.backgroundColor;
  const hasBackground = Boolean(bg && bg !== 'transparent');

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingLeft: '7%',
        paddingRight: '7%',
        paddingBottom: isPortrait ? '10%' : '7%',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: isPortrait ? 900 : 980,
          background: hasBackground ? bg : undefined,
          borderRadius: hasBackground ? 10 : undefined,
          padding: hasBackground ? (isPortrait ? '16px 20px' : '14px 18px') : undefined,
          boxShadow: hasBackground ? '0 10px 28px rgba(0,0,0,0.38)' : undefined,
        }}
      >
        <div
          style={{
            fontFamily,
            fontSize,
            fontWeight: 800,
            textAlign: 'center',
            lineHeight: 1.08,
            letterSpacing: 0,
            textShadow: '0 3px 12px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,0.9)',
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            columnGap: '0.28em',
            rowGap: '0.08em',
          }}
        >
          {chunk.words.map((w, i) => {
            const isActive = i === activeWordIndex;
            const isFuture = currentMs < w.startMs;
            const isPast = currentMs > w.endMs;

            const activeScale = interpolate(
              currentMs,
              [w.startMs, (w.startMs + w.endMs) / 2, w.endMs],
              [1, 1.04, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            const baseOpacity = isFuture ? 0.46 : isPast ? 0.82 : 1;
            const wordStyle: React.CSSProperties =
              isActive
                ? {
                    color: highlightColor,
                  }
                : { color };

            return (
              <span
                key={`${w.startMs}-${i}`}
                style={{
                  display: 'inline-block',
                  opacity: baseOpacity,
                  transform: isActive ? `scale(${activeScale})` : 'scale(1)',
                  ...wordStyle,
                }}
              >
                {w.text}
              </span>
            );
          })}
        </div>
      </div>
    </AbsoluteFill>
  );
};

// Full-frame dark field with the slide clip 16:9 full-width, centered
// vertically (1080x608 at y=656 in the 9:16 frame). Mounted inside a
// <Sequence> so the slide's own entrance animation plays from its t=0.
const SlideCutawayOverlay: React.FC<{ src: string }> = ({ src }) => (
  <AbsoluteFill
    style={{
      backgroundColor: SLIDE_FIELD_COLOR,
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <Video
      src={src}
      volume={0}
      style={{
        width: '100%',
        aspectRatio: '16 / 9',
        objectFit: 'contain',
      }}
    />
  </AbsoluteFill>
);

export const TalkingHeadEnhanced: React.FC<TalkingHeadEnhancedProps> = ({
  videoUrl,
  captions,
  captionStyle,
  fitMode = 'blur-contain',
  slideVideoUrl,
  slideCutaway,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const currentMs = (frame / fps) * 1000;

  const slideSrc = slideVideoUrl ? resolveMediaSrc(slideVideoUrl) : null;
  const cutawayFromFrame = Math.round((slideCutaway?.atSec ?? SLIDE_CUTAWAY_DEFAULT_AT_SEC) * fps);
  const cutawayDurationInFrames = Math.max(
    1,
    Math.round((slideCutaway?.durSec ?? SLIDE_CUTAWAY_DEFAULT_DUR_SEC) * fps),
  );
  // Rendered ABOVE the face video (covers it; face audio keeps playing) and
  // BELOW the captions layer. No slideVideoUrl -> no cutaway, current behavior.
  const slideCutawayLayer = slideSrc ? (
    <Sequence from={cutawayFromFrame} durationInFrames={cutawayDurationInFrames}>
      <SlideCutawayOverlay src={slideSrc} />
    </Sequence>
  ) : null;

  if (!videoUrl) {
    return (
      <AbsoluteFill
        style={{
          backgroundColor: '#000000',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#ffffff',
          fontFamily: FONTS.body,
          fontSize: 32,
        }}
      >
        Missing videoUrl
      </AbsoluteFill>
    );
  }

  const src = resolveMediaSrc(videoUrl);

  if (fitMode === 'cover') {
    return (
      <AbsoluteFill style={{ backgroundColor: '#000000' }}>
        <Video
          src={src}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />

        {slideCutawayLayer}

        {captions?.words?.length ? (
          <KaraokeCaptions captions={captions} currentMs={currentMs} style={captionStyle} />
        ) : null}
      </AbsoluteFill>
    );
  }

  return (
    <AbsoluteFill style={{ backgroundColor: '#05070a' }}>
      {/* Blur-fill background (muted) */}
      <AbsoluteFill
        style={{
          transform: 'scale(1.08)',
          filter: 'blur(34px) brightness(0.7) saturate(1.15)',
          opacity: 0.95,
        }}
      >
        <Video
          src={src}
          volume={0}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </AbsoluteFill>

      {/* Foreground talking-head (audio on) */}
      <AbsoluteFill
        style={{
          padding: '6%',
        }}
      >
        <Video
          src={src}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            borderRadius: 22,
            boxShadow: '0 22px 65px rgba(0,0,0,0.55)',
          }}
        />
      </AbsoluteFill>

      {slideCutawayLayer}

      {captions?.words?.length ? (
        <KaraokeCaptions captions={captions} currentMs={currentMs} style={captionStyle} />
      ) : null}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
